'use client';

import type { RowData } from '@tanstack/react-table';
import type { Column } from '@/lib/table-features';
import {
  endOfMonth,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths
} from 'date-fns';
import { CalendarIcon, XCircle } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import * as React from 'react';
import type { DateRange } from '@daypicker/react';
import { ar } from '@daypicker/react/locale';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { formatDate } from '@/lib/format';

type DateSelection = Date[] | DateRange;

function getIsDateRange(value: DateSelection): value is DateRange {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function parseAsDate(timestamp: number | string | undefined): Date | undefined {
  if (!timestamp) return undefined;
  const numericTimestamp =
    typeof timestamp === 'string' ? Number(timestamp) : timestamp;
  const date = new Date(numericTimestamp);
  return !Number.isNaN(date.getTime()) ? date : undefined;
}

function parseColumnFilterValue(value: unknown) {
  if (value === null || value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'number' || typeof item === 'string') {
        return item;
      }
      return undefined;
    });
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return [value];
  }

  return [];
}

interface DataTableDateFilterProps<TData extends RowData> {
  column: Column<TData, unknown>;
  title?: string;
  multiple?: boolean;
}

export function DataTableDateFilter<TData extends RowData>({
  column,
  title,
  multiple
}: DataTableDateFilterProps<TData>) {
  const locale = useLocale();
  const t = useTranslations('common');
  const dayPickerLocale = locale === 'ar' ? ar : undefined;
  const calendarDir = locale === 'ar' ? 'rtl' : 'ltr';
  const columnFilterValue = column.getFilterValue();

  const [isRange, setIsRange] = React.useState(multiple ?? false);

  const selectedDates = React.useMemo<DateSelection>(() => {
    if (!columnFilterValue) {
      return isRange ? { from: undefined, to: undefined } : [];
    }

    if (isRange) {
      const timestamps = parseColumnFilterValue(columnFilterValue);
      return {
        from: parseAsDate(timestamps[0]),
        to: parseAsDate(timestamps[1])
      };
    }

    const timestamps = parseColumnFilterValue(columnFilterValue);
    const date = parseAsDate(timestamps[0]);
    return date ? [date] : [];
  }, [columnFilterValue, isRange]);

  const applyRange = React.useCallback(
    (from: Date, to?: Date) => {
      if (isRange) {
        column.setFilterValue([from.getTime(), (to ?? from).getTime()]);
      } else {
        column.setFilterValue(from.getTime());
      }
    },
    [column, isRange]
  );

  const onSelectCalendar = React.useCallback(
    (date: Date | DateRange | undefined) => {
      if (!date) {
        column.setFilterValue(undefined);
        return;
      }

      if (isRange && !('getTime' in date)) {
        const from = date.from?.getTime();
        const to = date.to?.getTime();
        column.setFilterValue(from || to ? [from, to] : undefined);
      } else if (!isRange && 'getTime' in date) {
        column.setFilterValue(date.getTime());
      }
    },
    [column, isRange]
  );

  const onReset = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      column.setFilterValue(undefined);
    },
    [column]
  );

  const hasValue = React.useMemo(() => {
    if (isRange) {
      if (!getIsDateRange(selectedDates)) return false;
      return selectedDates.from || selectedDates.to;
    }
    if (!Array.isArray(selectedDates)) return false;
    return selectedDates.length > 0;
  }, [isRange, selectedDates]);

  const formatDateRange = React.useCallback(
    (range: DateRange) => {
      if (!range.from && !range.to) return '';
      if (range.from && range.to) {
        return `${formatDate(range.from, {}, locale)} - ${formatDate(range.to, {}, locale)}`;
      }
      return formatDate(range.from ?? range.to, {}, locale);
    },
    [locale]
  );

  const label = React.useMemo(() => {
    if (isRange) {
      if (!getIsDateRange(selectedDates)) return null;

      const hasSelectedDates = selectedDates.from || selectedDates.to;
      const dateText = hasSelectedDates
        ? formatDateRange(selectedDates)
        : t('dateFilter.selectRange');

      return (
        <span className='flex items-center gap-2'>
          <span>{title}</span>
          {hasSelectedDates && (
            <>
              <Separator
                orientation='vertical'
                className='mx-0.5 data-[orientation=vertical]:h-4'
              />
              <span>{dateText}</span>
            </>
          )}
        </span>
      );
    }

    if (getIsDateRange(selectedDates)) return null;

    const hasSelectedDate = selectedDates.length > 0;
    const dateText = hasSelectedDate
      ? formatDate(selectedDates[0], {}, locale)
      : t('dateFilter.selectDate');

    return (
      <span className='flex items-center gap-2'>
        <span>{title}</span>
        {hasSelectedDate && (
          <>
            <Separator
              orientation='vertical'
              className='mx-0.5 data-[orientation=vertical]:h-4'
            />
            <span>{dateText}</span>
          </>
        )}
      </span>
    );
  }, [selectedDates, isRange, formatDateRange, title, locale, t]);

  const today = new Date();

  const presets = React.useMemo(
    () => [
      {
        label: t('dateFilter.today'),
        apply: () => applyRange(today)
      },
      {
        label: t('dateFilter.yesterday'),
        apply: () => {
          const d = subDays(today, 1);
          applyRange(d, d);
        }
      },
      {
        label: t('dateFilter.thisWeek'),
        apply: () => applyRange(startOfWeek(today), today)
      },
      {
        label: t('dateFilter.last7Days'),
        apply: () => applyRange(subDays(today, 6), today)
      },
      {
        label: t('dateFilter.last28Days'),
        apply: () => applyRange(subDays(today, 27), today)
      },
      {
        label: t('dateFilter.thisMonth'),
        apply: () => applyRange(startOfMonth(today), today)
      },
      {
        label: t('dateFilter.lastMonth'),
        apply: () => {
          const lm = subMonths(today, 1);
          applyRange(startOfMonth(lm), endOfMonth(lm));
        }
      },
      {
        label: t('dateFilter.thisYear'),
        apply: () => applyRange(startOfYear(today), today)
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isRange, applyRange]
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='outline' size='sm' className='border-dashed'>
          {hasValue ? (
            <div
              role='button'
              aria-label={`Clear ${title} filter`}
              tabIndex={0}
              onClick={onReset}
              className='focus-visible:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-1 focus-visible:outline-none'
            >
              <XCircle />
            </div>
          ) : (
            <CalendarIcon />
          )}
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <div className='flex'>
          {/* Left panel: mode toggle + presets */}
          <div className='flex flex-col border-e'>
            <div className='flex items-center gap-2 border-b px-3 py-2.5'>
              <Checkbox
                id='date-range-mode'
                checked={isRange}
                onCheckedChange={(checked) => {
                  setIsRange(!!checked);
                  column.setFilterValue(undefined);
                }}
              />
              <Label
                htmlFor='date-range-mode'
                className='cursor-pointer text-sm font-normal'
              >
                {t('dateFilter.duration')}
              </Label>
            </div>
            <div className='flex flex-col py-1'>
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type='button'
                  onClick={preset.apply}
                  className='hover:bg-accent px-4 py-1.5 text-start text-sm'
                >
                  {preset.label}
                </button>
              ))}
              <div className='my-1 border-t' />
              <button
                type='button'
                onClick={() => column.setFilterValue(undefined)}
                className='hover:bg-accent px-4 py-1.5 text-start text-sm'
              >
                {t('dateFilter.reset')}
              </button>
            </div>
          </div>

          {/* Right panel: calendar */}
          {isRange ? (
            <Calendar
              autoFocus
              mode='range'
              locale={dayPickerLocale}
              dir={calendarDir}
              selected={
                getIsDateRange(selectedDates)
                  ? selectedDates
                  : { from: undefined, to: undefined }
              }
              onSelect={onSelectCalendar}
            />
          ) : (
            <Calendar
              autoFocus
              mode='single'
              locale={dayPickerLocale}
              dir={calendarDir}
              selected={
                !getIsDateRange(selectedDates) ? selectedDates[0] : undefined
              }
              onSelect={onSelectCalendar}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
